/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DetailsLogTab.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import CustomTabsV2 from "../../common/tabs/CustomTabsV2";
import LogsTab from "./LogsTab";
import EEForm from "./EEForm";
import RenderAsKeyValue from "../customComponents/RenderAsKeyValue";
import { convertUtcToTimeZone } from "../../../../utils/convertUtcToTimeZone";
import SWMForm from "./SWMForm";
import CustomFormTextFieldV2 from "../../common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../common/connectors/MultiselectConnectorV2";
import AttributesForm from "../../../../pages/labcar/Attributes/AttributesForm";

const getZoneDetailsLabel = (zoneDetails) => {
  if (Array.isArray(zoneDetails)) {
    const zoneNames = zoneDetails
      .map((item) => item?.zone_name || item?.zone?.zone_name)
      .filter(Boolean);

    return zoneNames.length > 0 ? zoneNames.join(", ") : "-";
  }

  return zoneDetails?.zone_name || zoneDetails?.zone?.zone_name || "-";
};

const getSelectedVariantIds = (selectedComponent, selectedSingleVariantDetails) => {
  const activeVariantId = selectedSingleVariantDetails?.variant_id;
  const variantIds = [
    activeVariantId,
    ...(Array.isArray(selectedComponent?.variant_ids)
      ? selectedComponent.variant_ids
      : []),
    ...(Array.isArray(selectedComponent?.vehicle_component_details?.variant_ids)
      ? selectedComponent.vehicle_component_details.variant_ids
      : []),
  ].filter((variantId) => variantId !== undefined && variantId !== null);

  return [...new Map(variantIds.map((variantId) => [String(variantId), variantId])).values()];
};

const getSelectedVariantOptions = (
  variantOptionList,
  selectedComponent,
  selectedSingleVariantDetails,
) => {
  const selectedVariantIds = new Set(
    getSelectedVariantIds(
      selectedComponent,
      selectedSingleVariantDetails,
    ).map(String),
  );

  return (variantOptionList || []).filter((option) =>
    selectedVariantIds.has(String(option?.value)),
  );
};

const getKeywordOptionsFromComponent = (selectedComponent) =>
  (selectedComponent?.keywords || []).map((keyword) => ({
    value: keyword?.id ?? keyword?.value ?? keyword,
    label: keyword?.name ?? keyword?.label ?? keyword,
  }));

const getKeywordValuesFromComponent = (selectedComponent) =>
  getKeywordOptionsFromComponent(selectedComponent)
    .map((keyword) => keyword.value)
    .filter((value) => value !== null && value !== undefined && value !== "");

function DetailsLogTab({
  title,
  getZoneEECountList,
  openEditDetailsDialog,
  setOpenEditDetailsDialog,
  handleEEComponentReplace,
  selectedEEComponentReplaceDetails,
  setSelectedEEComponentReplaceDetails,
  vsOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedZoneDetails,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  supplierOptionsList,
  logData,
  getLogsData,
  selectedComponent,
  zone_name,
  ChangeLogsList,
  type,
  totalLogsCount,
  selectedVariantsMany,
  handleSWModuleReplace,
  selectedSWModuleReplaceDetails,
  setSelectedSWModuleReplaceDetails,
  vfOptionsList,
  visibleTabs,
}) {
  const [expandLogs, setExpandLogs] = useState(false);
  const visibleTabNames = visibleTabs || ["Details", "Log", "Attribute"];
  const shouldShowLogTab = visibleTabNames.includes("Log");
  const getLogsDataRef = useRef(getLogsData);
  const selectedVariantOptions = getSelectedVariantOptions(
    variantOptionList,
    selectedComponent,
    selectedSingleVariantDetails,
  );
  const selectedVariantValues = selectedVariantOptions.map(
    (option) => option?.value,
  );
  const selectedKeywordOptions = getKeywordOptionsFromComponent(selectedComponent);
  const selectedKeywordValues = getKeywordValuesFromComponent(selectedComponent);

  const handleExpandLogs = (value) => {
    setExpandLogs(value);
  };

  useEffect(() => {
    getLogsDataRef.current = getLogsData;
  }, [getLogsData]);

  useEffect(() => {
    if (!openEditDetailsDialog || !shouldShowLogTab) {
      return;
    }

    getLogsDataRef.current({
      ee_id: logData?.ee_id,
      ...(type === "SWM" ? { swm_id: logData?.swm_id } : {}),
      pageNo: 1,
      pageSize: 25,
    });
  }, [
    logData?.ee_id,
    logData?.swm_id,
    openEditDetailsDialog,
    shouldShowLogTab,
    type,
  ]);
  return (
    <Box sx={{ width: "100%" }}>
      <CustomTabsV2
        key={visibleTabNames.join("|")}
        tabs={[
          {
            name: "Details",
            count: "",
            content: (
              <Box sx={{ p: 0 }}>
                <Box sx={{ p: 0 }}>
                  {type === "VS" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <CustomFormTextFieldV2
                        size={"small"}
                        label={"Assembly No"}
                        type={"text"}
                        fldType={"textR"}
                        value={selectedComponent?.assembly_number || ""}
                        setValue={() => {}}
                        disabled
                      />
                      <CustomSelectV2
                        size={"small"}
                        label={"Select Vehicle System"}
                        optionsList={[
                          {
                            value: selectedComponent?.name || "",
                            label: selectedComponent?.name || "",
                          },
                        ]}
                        selectedValue={selectedComponent?.name || ""}
                        handleOnChange={() => {}}
                        disabled
                      />
                      <CustomFormTextFieldV2
                        size={"small"}
                        label={"Description"}
                        type={"text"}
                        fldType={"text"}
                        value={selectedComponent?.description || ""}
                        setValue={() => {}}
                        disabled
                      />
                      <MultiselectConnectorV2
                        size={"small"}
                        label={"Select Suppliers"}
                        optionsList={(selectedComponent?.suppliers || []).map(
                          (item) => ({
                            value: item,
                            label: item,
                          }),
                        )}
                        selectedValues={selectedComponent?.suppliers || []}
                        setSelectedValues={() => {}}
                        placeholder={false}
                        disabled
                      />
                      <MultiselectConnectorV2
                        size={"small"}
                        label={"Keywords"}
                        optionsList={selectedKeywordOptions}
                        selectedValues={selectedKeywordValues}
                        setSelectedValues={() => {}}
                        placeholder={false}
                        disabled
                      />
                      <MultiselectConnectorV2
                        size={"small"}
                        label={"Select Variants"}
                        optionsList={selectedVariantOptions}
                        selectedValues={selectedVariantValues}
                        setSelectedValues={() => {}}
                        placeholder={false}
                        disabled
                      />
                    </Box>
                  )}
                  {type === "VC" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <CustomFormTextFieldV2
                        size={"small"}
                        label={"Component No"}
                        type={"text"}
                        fldType={"textR"}
                        value={selectedComponent?.component_number || ""}
                        setValue={() => {}}
                        disabled
                      />
                      <CustomSelectV2
                        size={"small"}
                        label={"Select Vehicle Component"}
                        optionsList={[
                          {
                            value: selectedComponent?.name || "",
                            label: selectedComponent?.name || "",
                          },
                        ]}
                        selectedValue={selectedComponent?.name || ""}
                        handleOnChange={() => {}}
                        disabled
                      />
                      <CustomFormTextFieldV2
                        size={"small"}
                        label={"Description"}
                        type={"text"}
                        fldType={"text"}
                        value={selectedComponent?.description || ""}
                        setValue={() => {}}
                        disabled
                      />
                      <MultiselectConnectorV2
                        size={"small"}
                        label={"Select Suppliers"}
                        optionsList={(selectedComponent?.suppliers || []).map(
                          (item) => ({
                            value: item,
                            label: item,
                          }),
                        )}
                        selectedValues={selectedComponent?.suppliers || []}
                        setSelectedValues={() => {}}
                        placeholder={false}
                        disabled
                      />
                      <MultiselectConnectorV2
                        size={"small"}
                        label={"Keywords"}
                        optionsList={selectedKeywordOptions}
                        selectedValues={selectedKeywordValues}
                        setSelectedValues={() => {}}
                        placeholder={false}
                        disabled
                      />
                      <MultiselectConnectorV2
                        size={"small"}
                        label={"Select Variants"}
                        optionsList={selectedVariantOptions}
                        selectedValues={selectedVariantValues}
                        setSelectedValues={() => {}}
                        placeholder={false}
                        disabled
                      />
                    </Box>
                  )}

                  {type === "EE" && (
                    <EEForm
                      title={title}
                      getZoneEECountList={getZoneEECountList}
                      openEEComponentReplaceDialog={openEditDetailsDialog}
                      setOpenEEComponentReplaceDialog={setOpenEditDetailsDialog}
                      handleEEComponentReplace={handleEEComponentReplace}
                      selectedEEComponentReplaceDetails={
                        selectedEEComponentReplaceDetails
                      }
                      setSelectedEEComponentReplaceDetails={
                        setSelectedEEComponentReplaceDetails
                      }
                      vsOptionsList={vsOptionsList}
                      setIsLinkSoftwareModulesScreen={
                        setIsLinkSoftwareModulesScreen
                      }
                      selectedZone={selectedZone}
                      selectedZoneDetails={selectedZoneDetails}
                      selectedEEComponentDetails={selectedEEComponentDetails}
                      getEEComponentsList={getEEComponentsList}
                      getSWModulesList={getSWModulesList}
                      selectedProjectDetails={selectedProjectDetails}
                      selectedSingleVariantDetails={
                        selectedSingleVariantDetails
                      }
                      variantOptionList={variantOptionList}
                      supplierOptionsList={supplierOptionsList}
                      disableFields={[
                        "assemblyNo",
                        "supplierName",
                        "version",
                        "description",
                        "summary",
                        "vehicleSystems",
                        "asilClass",
                        "keywords",
                        "variants",
                        "reason",
                      ]}
                    />
                  )}

                  {type === "SWM" && (
                    <SWMForm
                      supplierOptionsList={supplierOptionsList}
                      title={"Replace SW Module"}
                      selectedVariantsMany={selectedVariantsMany}
                      variantOptionList={variantOptionList}
                      selectedProjectDetails={selectedProjectDetails}
                      selectedEEComponentDetails={selectedEEComponentDetails}
                      openSWModuleReplaceDialog={openEditDetailsDialog}
                      setOpenSWModuleReplaceDialog={setOpenEditDetailsDialog}
                      handleSWModuleReplace={handleSWModuleReplace}
                      selectedSWModuleReplaceDetails={
                        selectedSWModuleReplaceDetails
                      }
                      setSelectedSWModuleReplaceDetails={
                        setSelectedSWModuleReplaceDetails
                      }
                      vfOptionsList={vfOptionsList}
                      setIsLinkSoftwareModulesScreen={
                        setIsLinkSoftwareModulesScreen
                      }
                      selectedZone={selectedZone}
                      getSWModulesList={getSWModulesList}
                      selectedSingleVariantDetails={
                        selectedSingleVariantDetails
                      }
                      disableFields={[
                        "number",
                        "version",
                        "description",
                        "summary",
                        "supplierName",
                        "keywords",
                        "vehicleFunction",
                      ]}
                    />
                  )}
                </Box>
                <Box>
                  <Typography>
                    <RenderAsKeyValue
                      keyValues={{
                        "Zone Details":
                          zone_name ||
                          getZoneDetailsLabel(selectedComponent?.zone_details),
                        "Created at": convertUtcToTimeZone(
                          selectedComponent?.created_at,
                        ),
                        "Modified at": convertUtcToTimeZone(
                          selectedComponent?.updated_at,
                        ),
                      }}
                    />
                  </Typography>
                </Box>
              </Box>
            ),
          },
          {
            name: "Log",
            content: (
              <LogsTab
                logs={ChangeLogsList}
                expanded={expandLogs}
                setExpanded={handleExpandLogs}
              />
            ),
            count: totalLogsCount || 0,
          },
          {
            name: "Attribute",
            content: (
              <Box>
                <AttributesForm
                  type={"VIEW"}
                  attributeFromApi={selectedComponent?.attributes || []}
                  getFormData={() => {}}
                />
              </Box>
            ),
          },
        ].filter((tab) => visibleTabNames.includes(tab.name))}
      />
    </Box>
  );
}

DetailsLogTab.propTypes = {
  title: PropTypes.string,
  getZoneEECountList: PropTypes.func,
  openEditDetailsDialog: PropTypes.bool,
  setOpenEditDetailsDialog: PropTypes.func,
  handleEEComponentReplace: PropTypes.func,
  selectedEEComponentReplaceDetails: PropTypes.object,
  setSelectedEEComponentReplaceDetails: PropTypes.func,
  vsOptionsList: PropTypes.array,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  selectedZone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedZoneDetails: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.object,
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,
  logData: PropTypes.shape({
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  getLogsData: PropTypes.func,
  selectedComponent: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    assembly_number: PropTypes.string,
    component_number: PropTypes.string,
    suppliers: PropTypes.array,
    attributes: PropTypes.array,
    zone_details: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    variant_ids: PropTypes.array,
    vehicle_component_details: PropTypes.object,
    keywords: PropTypes.array,
  }),
  zone_name: PropTypes.string,
  ChangeLogsList: PropTypes.array,
  type: PropTypes.string,
  totalLogsCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedVariantsMany: PropTypes.array,
  handleSWModuleReplace: PropTypes.func,
  selectedSWModuleReplaceDetails: PropTypes.object,
  setSelectedSWModuleReplaceDetails: PropTypes.func,
  vfOptionsList: PropTypes.array,
  visibleTabs: PropTypes.arrayOf(PropTypes.string),
};

export default DetailsLogTab;
